import React from "react";
import {
  Box,
  FormControlLabel,
  FormLabel,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { useSelector } from "react-redux";
import uploadIcon from "../../assets/upload.png";
import Thumbnail from "../../assets/Thumbnail.png";
import { darkTheme, lightTheme } from "../../theme/Theme";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import useThemeRender from "../../theme/useThemeRender";
import { setInternalFileDocument } from "../../slices/adminPageSlice.js";
import { Card, Radio } from "pepsico-ds";
import { useStepOneUpload } from "./useStepOneUpload";
import {
  renderProgressBar as renderProgressBarBase,
  ThumbnailFileInput,
  ThumbnailPreview,
} from "./uploadRenderHelpers";

const StepOneForm = ({ internalData, setInternalData, setFileType }) => {
  const { internalFileDocument } = useSelector((store) => store.adminpage);
  const isMobile = useMediaQuery("(max-width:600px)");

  const {
    contentType,
    setContentType,
    isUploadingDocument,
    uploadPct,
    failedUpload,
    setFailedUpload,
    isUploadingThumbnail,
    thumbnailUploadPct,
    isDragging,
    setIsDragging,
    thumbnailInputRef,
    thumbnail,
    isDarkTheme,
    getThemeColor,
    dropzoneStyle,
    handlePreview,
    handleLinkChange,
    handleCancelUpload,
    handleRetryUpload,
    getRootProps,
    getInputProps,
    handleThumbnailUpload,
    handleDocumentDelete,
  } = useStepOneUpload({
    formData: internalData,
    setFormData: setInternalData,
    setFileType,
    fileDocument: internalFileDocument,
    setFileDocumentAction: setInternalFileDocument,
    // Preserved exactly as the original code dispatched it — not touching this as part
    // of the dedup, even though the literal reads oddly for the *internal* form.
    fileUploadPageName: "external",
    hasContentTypeSyncEffect: true,
    parseFileIdAsInt: true,
    thumbnailValidation: true,
    awaitDocumentDelete: false,
    logFileUploadedEffect: true,
  });

  // StepOneForm's progress bar text/cancel-button carry an extra `fontFamily: "inherit"`
  // sx that ExternalStepOneForm's never had — preserved via the shared helper's option.
  const renderProgressBar = (value, onCancel) =>
    renderProgressBarBase(value, onCancel, { withInheritedFontFamily: true });

  const handleChange = (val) => {
    setContentType(val === "LINK" ? "LINK" : "DOCUMENT");
    setInternalData((prevData) => ({
      ...prevData,
      contentTypeId: val,
    }));
  };

  const renderDropzone = (label) => {
    const handleDragOver = (event) => {
      event.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = () => {
      setIsDragging(false);
    };

    const handleDrop = (event) => {
      event.preventDefault();
      setIsDragging(false);

      const file = event.dataTransfer.files[0]; // Get the first dropped file
      if (file) {
        handleThumbnailUpload({ target: { files: [file] } }); // Simulate file input change event
      }
    };

    return (
      <Box>
        <FormLabel
          sx={{
            color: getThemeColor(
              lightTheme.textColorLayer1,
              darkTheme.textColorLayer1
            ),
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "18px",
            fontFamily: "inherit",
          }}
        >
          {label}
        </FormLabel>
        <Box
          sx={{
            display: "flex",
            mt: 2,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "8px",
            border: `1px dashed ${isDragging ? "#005CBC" : "#D7E5F8"}`,
            padding: "24px",
            width: { xs: "100%", sm: "50%" },
            background: getThemeColor(
              lightTheme.insideCardBackgroundColorLayer3,
              darkTheme.insideCardBackgroundColorLayer3
            ),
            position: "relative",
            transition: "border 0.3s ease-in-out",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isUploadingThumbnail ? (
            renderProgressBar(thumbnailUploadPct)
          ) : thumbnail ? (
            <ThumbnailPreview
              thumbnail={thumbnail}
              thumbnailInputRef={thumbnailInputRef}
              isUploadingThumbnail={isUploadingThumbnail}
              handleThumbnailUpload={handleThumbnailUpload}
            />
          ) : (
            <>
              <img src={Thumbnail} alt="Thumbnail Icon" />
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  color: getThemeColor(
                    lightTheme.textColorLayer1,
                    darkTheme.textColorLayer1
                  ),
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "18px",
                  fontFamily: "inherit",
                }}
              >
                {isDragging
                  ? "Drop your file here!"
                  : "Drag and drop your file here (only .jpg, .png)"}
              </Typography>
              <Button
                variant="text"
                sx={{
                  mt: 2,
                  borderRadius: "24px",
                  border: "2px solid #005CBC",
                  fontWeight: 700,
                  py: 1,
                  px: 2,
                  fontSize: "12px",
                  lineHeight: "18px",
                  fontFamily: "inherit",
                }}
                startIcon={<UploadOutlinedIcon />}
                onClick={() => thumbnailInputRef.current.click()}
                disabled={isUploadingThumbnail}
              >
                Upload File
              </Button>
              <ThumbnailFileInput
                inputRef={thumbnailInputRef}
                onChange={handleThumbnailUpload}
                disabled={isUploadingThumbnail}
              />
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Card
      ellipsis={false}
      isDark={isDarkTheme}
      icon=""
      title="Step 1: Upload"
      style={{ borderRadius: "8px" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: "8px",
        }}
      >
        <FormLabel
          sx={{
            color: useThemeRender("textColorLayer1"),
            fontSize: "12px",
            lineHeight: "18px",
            fontFamily: "inherit",
          }}
        >
          Content type
        </FormLabel>
        <Radio
          items={["Upload", "Link"]}
          orientation="horizontal"
          reference="radio button"
          showText
          size="small"
          onUpdate={(index, value) => handleChange(value?.toLocaleUpperCase())}
          selectedItemIndex={contentType ? (contentType === "LINK" ? 1 : 0) : 0}
          isDark={isDarkTheme}
          // style={{margin: '8px 0'}}
        />

        {contentType === "LINK" && (
          <>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                mt: 1,
                mb: 0.5,
                fontFamily: "var(--Typography-Font-Family-Body, Inter)",
                //color: isDarkTheme && darkTheme.textColorLayer1,
              }}
            >
              Link
            </Typography>
            <TextField
              placeholder="Paste Link"
              sx={{
                width: isMobile ? "100%" : "50%",
                "& .MuiOutlinedInput-input": {
                  padding: "0px 8px",
                  lineHeight: "20px",
                  fontSize: "14px",
                  height: "32px",
                },
              }}
              value={internalData.attachmentLinkUrl}
              onChange={handleLinkChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <LinkIcon sx={{ fontSize: "16px" }} />
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}

        {contentType === "DOCUMENT" && (
          <>
            <Box>
              {!internalFileDocument ? (
                <>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ width: { xs: "100%", sm: "50%" } }}
                  >
                    <FormLabel
                      sx={{
                        color: getThemeColor(
                          lightTheme.textColorLayer1,
                          darkTheme.textColorLayer1
                        ),
                        fontSize: "12px",
                        lineHeight: "18px",
                        fontFamily: "inherit",
                      }}
                    >
                      Document
                    </FormLabel>
                    <Box display="flex" justifyContent="space-between">
                      <FormLabel
                        sx={{
                          color: getThemeColor(
                            lightTheme.textColorLayer4,
                            darkTheme.textColorLayer4
                          ),
                          fontSize: "10px",
                          fontFamily: "inherit",
                        }}
                      >
                        *Required
                      </FormLabel>
                    </Box>
                  </Box>
                  {failedUpload && !isUploadingDocument ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                        mb: 2,
                        width: { xs: "100%", sm: "50%" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <ErrorOutlineOutlinedIcon
                          fontSize="small"
                          sx={{ mr: 1, color: "error.main" }}
                        />
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontFamily: "inherit",
                              lineHeight: "18px",
                            }}
                          >
                            {failedUpload.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "10px",
                              color: "error.main",
                              fontFamily: "inherit",
                            }}
                          >
                            Upload failed — you can retry
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<AutorenewOutlinedIcon />}
                          sx={{
                            fontWeight: 700,
                            fontSize: "12px",
                            fontFamily: "inherit",
                          }}
                          onClick={handleRetryUpload}
                        >
                          Retry
                        </Button>
                        <IconButton onClick={() => setFailedUpload(null)}>
                          <DeleteOutlineOutlinedIcon
                            fontSize="small"
                            color="primary"
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                  <Box
                    {...getRootProps()}
                    sx={{ mt: 2, mb: 2, ...dropzoneStyle }}
                  >
                    {isUploadingDocument ? (
                      renderProgressBar(uploadPct, handleCancelUpload)
                    ) : (
                      <img src={uploadIcon} alt="Upload Icon" />
                    )}
                    <input {...getInputProps()} />
                    <Typography
                      sx={{
                        color: getThemeColor(
                          lightTheme.textColorLayer1,
                          darkTheme.textColorLayer1
                        ),
                        fontSize: "12px",
                        fontWeight: 400,
                        lineHeight: "18px",
                        fontFamily: "inherit",
                      }}
                    >
                      Drag and drop your file here
                    </Typography>
                    <Button
                      variant="text"
                      sx={{
                        mt: 2,
                        borderRadius: "24px",
                        border: "2px solid #005CBC",
                        fontWeight: 700,
                        py: 1,
                        px: 2,
                        fontSize: "12px",
                        lineHeight: "18px",
                        fontFamily: "inherit",
                      }}
                      startIcon={<UploadOutlinedIcon />}
                      disabled={isUploadingDocument}
                    >
                      Upload File
                    </Button>
                  </Box>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: { xs: "100%", sm: "50%" },
                    height: { sm: "32px" },
                    background: getThemeColor(
                      lightTheme.insideCardBackgroundColorLayer3,
                      darkTheme.insideCardBackgroundColorLayer3
                    ),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={handlePreview}
                  >
                    <InsertDriveFileOutlinedIcon
                      fontSize="small"
                      sx={{ marginRight: 1, color: "primary.main" }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontFamily: "inherit",
                        lineHeight: "18px",
                      }}
                    >
                      {internalFileDocument?.name}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() =>
                      handleDocumentDelete(internalFileDocument?.fileId)
                    }
                  >
                    <DeleteOutlineOutlinedIcon
                      fontSize="small"
                      color="primary"
                    />
                  </IconButton>
                </Box>
              )}
            </Box>
            {renderDropzone("Thumbnail upload")}
          </>
        )}

        {contentType === "LINK" && renderDropzone("Thumbnail upload")}
      </Box>
    </Card>
  );
};

export default StepOneForm;
